//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import HeaderControl, { type HeaderControlTier } from './HeaderControl';
import { runningDescription } from './toolEntries';
import PanelIcon from '@/components/Panels/PanelIcon';
import { panelLauncherId } from '@/components/Panels/panelIds';
import { usePanels } from '@/hooks/usePanels';

interface PanelPillProps {
  panelId: string;
  // The pill's visible text and, always, its accessible name. Defaults to
  // the panel's registered title; a launcher passes one only when it must
  // name the panel before the registration lands, since the two render as
  // siblings.
  label?: string;
  isDisabled?: boolean;
  // The user asked for glyphs alone. Honored only where this pill has a
  // glyph that identifies it; see isLabelHidden below.
  isIconOnly?: boolean;
  // The user asked for names alone. Always honorable, since every pill has
  // a label, so it passes straight through. Never set both.
  isTitleOnly?: boolean;
  // Why the pill is disabled, for a description that would otherwise say
  // nothing.
  disabledReason?: string;
}

const tierFor = (
  isOpen: boolean, isMounted: boolean
): HeaderControlTier => {
  if (isOpen) return 'filled';
  if (isMounted) return 'outlined';
  return 'ghost';
};

/**
 * A header launcher for one panel.
 *
 * Lifecycle is drawn as escalating enclosure -- ghost when nothing has been
 * mounted, outlined while the panel is parked, filled while it is on screen --
 * so state never rests on color alone.
 *
 * A click toggles between open and iconified and never closes: closing
 * discards whatever a plugin's frame is holding, and that has to be a
 * deliberate act on the panel itself rather than a stray click up here.
 *
 * Mounted-ness rides the description rather than the accessible name. A name
 * that changes under a user as their panel mounts is its own defect.
 */
const PanelPill = ({
  panelId, label: givenLabel, isDisabled = false, disabledReason,
  isIconOnly = false, isTitleOnly = false
}: PanelPillProps) => {
  const {
    openPanel, iconifyPanel, isPanelOpen, isPanelMounted, getPanelSpec
  } = usePanels();

  const isOpen = isPanelOpen(panelId);
  const isMounted = isPanelMounted(panelId);
  // Drawn from the panel's own declaration rather than chosen here, so a
  // launcher and the panel it opens cannot disagree about what they are.
  const spec = getPanelSpec(panelId);
  const label = givenLabel ?? spec?.title ?? '';
  // A generic glyph is the one every plugin that declared no icon, or an icon
  // this host lacks, draws: on a rail of pills it distinguishes nothing, so
  // it is left off here. A list row keeps it, having the name beside it.
  const icon = spec?.isIconGeneric ? undefined : spec?.icon;
  // The preference arrives from the rail; whether this pill can honor it is
  // the pill's own business. Only a glyph that identifies this panel may
  // stand in for its name, and a panel with none would draw an empty button.
  const isLabelHidden = isIconOnly && icon !== undefined;

  const handleClick = () => {
    if (isOpen) iconifyPanel(panelId);
    else openPanel(panelId);
  };

  const description = () => {
    if (isDisabled && disabledReason) return `${label} \u2014 ${disabledReason}`;
    return runningDescription(label, isMounted, isOpen);
  };

  return (
    <HeaderControl
      // The panel host aims a dismissal's focus here when the panel has no
      // recorded invoker -- one pill per panel renders at a time (the rail
      // or, on narrow viewports, chat's standalone pill), so the id is
      // unique.
      id={panelLauncherId(panelId)}
      icon={icon ? <PanelIcon icon={icon} /> : null}
      label={label}
      isLabelHidden={isLabelHidden}
      isIconHidden={isTitleOnly}
      description={description()}
      tier={tierFor(isOpen, isMounted)}
      isPressed={isOpen}
      isDisabled={isDisabled}
      onClick={handleClick}
    />
  );
};

export default PanelPill;
